import { NavLink, useNavigate, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import {
  useHtzReportShowQuery,
  useHtzReportUpdateStateMutation,
} from "@/services/backend/htz/report/api/report-api";
import { Report } from "@/services/backend/htz/report/types/report";
import { Summary } from "@/services/backend/htz/summary/types/summary";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  ErrValidationFailedContextProvider,
  joinFieldName,
  useResetFieldsOnChange,
} from "@/shared/lib/errors/err-validation-failed-context";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { ReportPDF } from "@/routes/processes/[processId]/htz/summaries/[summaryId]/items/[itemId]/report/_components/report-pdf";
import { useSummaryItemReport } from "@/services/backend/htz/summary/hooks/use-summary-item-report";
import { SummaryBreadcrumbs } from "@/routes/processes/[processId]/htz/summaries/_components/summary-breadcrumbs";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentStateAlert } from "@/shared/bsm/core/document/components/ui/document-state-alert";
import { DocumentStateDropdown } from "@/shared/bsm/core/document/components/ui/document-state-dropdown";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { SummaryItemReportDeleteDialog } from "@/services/backend/htz/summary/components/summary-item-report-delete-dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";

/**
 * ReportRoute shows the Anlagenbericht summarized by an item of an
 * Inspektionsbericht. The report is resolved through the item in the URL.
 */
export function ReportRoute() {
  const { summaryId, itemId } = useParams();
  const {
    summary,
    item,
    reportId,
    isLoading: summaryIsLoading,
    error: summaryError,
    notFound,
  } = useSummaryItemReport(summaryId!, itemId!);

  const {
    data: report,
    isLoading: reportIsLoading,
    error: reportError,
  } = useHtzReportShowQuery({ id: reportId! }, { skip: !reportId });

  const breadcrumbs = summary && (
    <SummaryBreadcrumbs summary={summary} reportId={reportId} />
  );

  const error = summaryError ?? reportError;

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {(summaryIsLoading || reportIsLoading) && <DocumentPageSkeleton />}
      {summary && notFound && <ReportNotFound summary={summary} />}
      {summary && item && report && (
        <PersistenceContextProvider>
          <ErrValidationFailedContextProvider>
            <ReportPage summary={summary} itemId={item.id} report={report} />
          </ErrValidationFailedContextProvider>
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

// The results and recommendations of the report items are derived from the
// assessments, which the user edits on other tabs.
const derivedItemFields = [
  "structuralInspectionResult",
  "visualInspectionResult",
  "microbialInspectionResult",
  "result",
  "recommendations",
] as const;

function useResetItemErrorsOnChange(report: Report) {
  useResetFieldsOnChange(
    Object.fromEntries(
      report.items.flatMap((item) =>
        derivedItemFields.map((field) => [
          joinFieldName("items", item.id, field),
          item[field],
        ]),
      ),
    ),
  );
}

function summaryItemsPath(summary: Summary) {
  return `/processes/htz/${summary.processId!}/summaries/${summary.id}/items`;
}

function BackToSummaryItemsButton({ summary }: { summary: Summary }) {
  return (
    <Button asChild variant="outline" className="whitespace-normal">
      <NavLink to={summaryItemsPath(summary)}>
        <ArrowLeft className="size-4" />
        <span>{t("Inspektionsbericht")}</span>
      </NavLink>
    </Button>
  );
}

function ReportNotFound({ summary }: { summary: Summary }) {
  return (
    <div className="grid justify-items-start gap-4">
      <Alert variant="destructive">
        <AlertTitle>{t("Nicht gefunden")}</AlertTitle>
        <AlertDescription>
          {t("Dieser Anlagenbericht ist nicht Teil des Inspektionsberichts.")}
        </AlertDescription>
      </Alert>
      <BackToSummaryItemsButton summary={summary} />
    </div>
  );
}

function ReportPage({
  summary,
  itemId,
  report,
}: {
  summary: Summary;
  itemId: string;
  report: Report;
}) {
  const navigate = useNavigate();
  useResetItemErrorsOnChange(report);

  // The field names list the top-level keys of the fields on each tab, see
  // report.ValidateReadyForCustomer in the backend.
  const links = [
    {
      to: "data",
      label: t("Bericht"),
      fieldName: [
        "referenceNumber",
        "date",
        "createdBy",
        "createdByCompany",
        "objectAddress",
        "createdFor",
        "airHandlingUnitId",
        "laboratories",
        "inspectionDate",
        "inspectionConductedBy",
        "inspectionConductedByStaff",
        "lastInspectionDate",
        "nextInspectionDate",
        "vdi6022Judgement",
      ],
    },
    {
      to: "items",
      label: t("Komponenten"),
      fieldName: ["items", "ahuImageFileId", "airHandlingUnitId"],
    },
    {
      to: "microbial",
      label: t("Mikrobiologisch"),
      fieldName: ["microbialAssessments"],
    },
    {
      to: "structural",
      label: t("Konstruktiv"),
      fieldName: ["structuralAssessments"],
    },
    { to: "visual", label: t("Visuell"), fieldName: ["visualAssessments"] },
    { to: "photos", label: t("Fotos"), fieldName: ["photos"] },
    { to: "files", label: t("Dateien"), fieldName: [] },
  ];

  return (
    <DocumentPage>
      <DocumentStateAlert document={report} />
      <DocumentPageHeader>
        <BackToSummaryItemsButton summary={summary} />
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              fieldName={link.fieldName}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          {!isImmutable(report) && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <CpyDialog
                destinationEntityName={CpyName.HTZReport}
                destinationId={report.id}
                description={t(
                  "Es können Daten aus Positionen von Arbeitsscheinen aus diesem Vorgang übernommen werden.",
                )}
                initialParams={{
                  process: { active: true, values: [report.processId!] },
                }}
              />
              {!isImmutable(summary) && (
                <SummaryItemReportDeleteDialog
                  summaryId={summary.id}
                  itemId={itemId}
                  onDeleted={() => navigate(summaryItemsPath(summary))}
                />
              )}
            </>
          )}
          <DocumentStateDropdown
            document={report}
            mutation={useHtzReportUpdateStateMutation}
          />
          <ReportPDF report={report} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
